import { VIEW_KINDS, type ViewKind } from './view';

/** キーボードイベントのうち、視点の切り替えの判定に使う部分。 */
export interface ShortcutEvent {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly repeat: boolean;
  /** イベントの発生元（DOM の要素、またはテスト用の `{ tagName }`） */
  readonly target: unknown;
}

/** 発生元から読む、要素の種類の情報。 */
interface ElementLike {
  readonly tagName?: unknown;
  readonly isContentEditable?: unknown;
}

/** 文字を入力・選択する要素。ここでのキー入力は、視点の切り替えにしない。 */
const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * 数字キー（1〜5）に対応する視点を返す（FR-CAM-011）。表示順のとおり：
 * 1 全体俯瞰、2 追従（後方）、3 追従（前方）、4 追従（側方）、5 乗客視点。
 *
 * 修飾キー付き・キーの押しっぱなし・入力欄での入力は対象外（`undefined`）。
 */
export function viewForKey(event: ShortcutEvent): ViewKind | undefined {
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.repeat) {
    return undefined;
  }
  const target = (typeof event.target === 'object' ? event.target : null) as ElementLike | null;
  const tag = typeof target?.tagName === 'string' ? target.tagName.toUpperCase() : undefined;
  if (target?.isContentEditable === true || (tag !== undefined && TYPING_TAGS.has(tag))) {
    return undefined;
  }
  if (!/^[1-9]$/.test(event.key)) return undefined;
  return VIEW_KINDS[Number(event.key) - 1];
}

/** 視点に対応する数字キー（表示用）。 */
export function keyForView(view: ViewKind): string {
  return String(VIEW_KINDS.indexOf(view) + 1);
}

/** 走行中の操作のキー。 */
export type RideControlKey = 'throttle' | 'boost';

/**
 * 走行中の操作に対応するキー（FR-SIM-008）：↑ は押している間の加速、B はブースト。
 * 修飾キー付き・入力欄での入力は対象外（`undefined`）。押しっぱなしの繰り返しは、呼び出し側で無視する。
 */
export function rideControlForKey(event: ShortcutEvent): RideControlKey | undefined {
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return undefined;
  const target = (typeof event.target === 'object' ? event.target : null) as ElementLike | null;
  const tag = typeof target?.tagName === 'string' ? target.tagName.toUpperCase() : undefined;
  if (target?.isContentEditable === true || (tag !== undefined && TYPING_TAGS.has(tag))) {
    return undefined;
  }
  if (event.key === 'ArrowUp') return 'throttle';
  if (event.key === 'b' || event.key === 'B') return 'boost';
  return undefined;
}

/**
 * 全画面のとき、H キーで「すっきり」（ボタン・計器・ウィンドウをすべて隠す）を切り替える。
 * 修飾キー付き・押しっぱなし・入力欄での入力は対象外。
 */
export function isCleanToggleKey(event: ShortcutEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.repeat) {
    return false;
  }
  const target = (typeof event.target === 'object' ? event.target : null) as ElementLike | null;
  const tag = typeof target?.tagName === 'string' ? target.tagName.toUpperCase() : undefined;
  if (target?.isContentEditable === true || (tag !== undefined && TYPING_TAGS.has(tag))) {
    return false;
  }
  return event.key === 'h' || event.key === 'H';
}
